import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spacer,
  Spinner,
  useMediaQuery,
} from "@chakra-ui/react";
import { FaSearch } from "react-icons/fa";
import Heading from "ui/Heading";
import Text from "ui/Text";
import theme from "ui/theme/theme";
// import SeoTags from 'components/SEO/SeoTags';
import { StyledTable, StyledTd, StyledTh, StyledTr } from "./Management.styles";
import SelectorIcon from "ui/SelectorIcon";
import { convertLocalDateTime } from "utils/dynDateUtils";
import Pagination from "components/Pagination/Pagination";
import BlogActionMenu from "./components/BlogActionMenu";

const SORT_BY_COLUMN = ["updated_time"];
const DefaultFilters = Object.freeze({ statusFilters: [], levelFilters: [] });
const PAGE_SIZE = 20;

function BlogManagement() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [blogCount, setBlogCount] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState(
    JSON.parse(JSON.stringify(DefaultFilters))
  );
  const [asc, setAsc] = useState(true);
  const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);

  const params = qs.parse(useLocation().search, {
    ignoreQueryPrefix: true,
    decoder: (c) => c,
  });

  async function searchBlogs(keywords) {
    setIsSearching(true);
    setFilters({ ...filters, keywords });
  }

  async function getBlogs() {
    const reqestBody = {
      keywords: filters?.keywords ?? "",
      page: page - 1,
      size: PAGE_SIZE,
      sortBy: params?.sortName,
      ascending: asc,
    };
    // console.log('==========request blog body: ', reqestBody);

    api
      .post(`${API_BASE}/api/blog/search`, reqestBody)
      .then((res) => {
        if (res.status == 200) {
          // console.log('+++++++++, search blogs response: ', res.data);
          setBlogs(res.data?.blogs);
          setBlogCount(res.data?.total);
        }
      })
      .catch((err) => console.log("failed to fetch relay blogs: ", err))
      .finally(() => setIsSearching(false));
  }

  useEffect(() => {
    getBlogs();
  }, [page, filters, asc]);

  function updateParams(newParams) {
    const updatedParams = { ...params, ...newParams };
    const stringifiedParams = qs.stringify(updatedParams, {
      skipNulls: true,
      format: "RFC1738",
      encode: false,
    });
    navigate(`?${stringifiedParams}`, { replace: true });
  }

  function handleSortClick(key) {
    if (params.sortName === key) {
      setAsc((prev) => !prev);
    } else {
      setAsc(true);
    }
  }

  function handleSearch(e) {
    if (e.code === "Enter") {
      searchBlogs(e.target.value);
    }
  }

  function ResultModule() {
    return <div>Result Module</div>;
  }

  function updateBlogStatus(blogId, status) {
    api
      .patch(`${API_BASE}/api/blog/status?blogId=${blogId}&status=${status}`)
      .then((res) => {
        if (res.status === 200) {
          // console.log(
          //   'successfully update blog: ' + blogId + ' status to: ' + status
          // );
          getBlogs();
        }
      });
  }

  function arrayToString(stringsArray) {
    if (!Array.isArray(stringsArray) || !stringsArray) {
      return "";
    }
    return stringsArray.join("; ");
  }

  return (
    <Box>
      {/* <SeoTags t="Blog Management | Dynivy Academy" /> */}
      <HStack>
        <Heading variant="h2">Blog Management</Heading>
        <Spacer />
        <Button
          as={Link}
          to={`/create-blog`}
          variant="outline"
          colorScheme="green"
        >
          New Blog
        </Button>
      </HStack>

      {blogCount && blogCount > 0 ? (
        <>
          <Flex
            alignItems="center"
            mt="32px"
            mb="24px"
            justifyContent="space-between"
          >
            <InputGroup w="60%">
              <InputLeftElement ml="10px" mt="6px">
                {isSearching ? (
                  <Spinner />
                ) : (
                  <FaSearch
                    onClick={() => searchBlogs(query)}
                    style={{ cursor: "pointer" }}
                  />
                )}
              </InputLeftElement>
              <Input
                // ref={searchRef}
                id="searchBlogs"
                onKeyDown={handleSearch}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                paddingLeft="56px"
                type="text"
                placeholder="Search blog title, category, tags, etc."
                maxW={{ base: "unset", md: "354px" }}
                w="100%"
                h="48px"
                _placeholder={{ fontSize: "1rem" }}
                // borderRadius="52px"
                bg="gray.50"
              />
            </InputGroup>
          </Flex>
          {!isDesktop && (
            <Flex mb="24px">
              <ResultModule />
            </Flex>
          )}
          {isDesktop ? (
            <StyledTable>
              <StyledTr>
                <StyledTh className="leftAlign" isGray p="20px 14px">
                  <Text size="medium" fontWeight="bold" mr="8px">
                    Title
                  </Text>
                </StyledTh>
                <StyledTh className="leftAlign" isGray p="20px 14px">
                  <Text size="medium" fontWeight="bold" mr="8px">
                    Category
                  </Text>
                </StyledTh>
                <StyledTh className="leftAlign" isGray p="20px 14px">
                  <Text size="medium" fontWeight="bold" mr="8px">
                    Tags
                  </Text>
                </StyledTh>
                <StyledTh className="leftAlign" isGray p="20px 14px">
                  <Text size="medium" fontWeight="bold" mr="8px">
                    Featured
                  </Text>
                </StyledTh>
                <StyledTh className="leftAlign" isGray p="20px 14px">
                  <Text size="medium" fontWeight="bold" mr="8px">
                    Status
                  </Text>
                </StyledTh>
                <StyledTh className="leftAlign" isGray p="20px 14px">
                  <Flex alignItems="center">
                    <Text size="medium" fontWeight="bold" mr="8px">
                      Updated Time
                    </Text>
                    <Flex
                      as="button"
                      type="button"
                      onClick={() => {
                        updateParams({ sortName: SORT_BY_COLUMN[0] });
                        handleSortClick(SORT_BY_COLUMN[0]);
                      }}
                    >
                      <SelectorIcon
                        asc={asc}
                        selected={params.sortName === SORT_BY_COLUMN[0]}
                      />
                    </Flex>
                  </Flex>
                </StyledTh>
                <StyledTh className="leftAlign" isGray p="20px 14px">
                  <Text size="medium" fontWeight="bold" mr="8px">
                    Action
                  </Text>
                </StyledTh>
              </StyledTr>
              <tbody>
                {blogs &&
                  blogs?.map((item, idx) => (
                    <StyledTr key={item.blogId}>
                      <StyledTd isEven={idx % 2 === 0} p="8px 14px">
                        <Text
                          size="medium"
                          color="semantics.link"
                          fontWeight="bold"
                        >
                          <Link to={`/blog/${item.slug}`}>
                            {item.title || item.blogId}
                          </Link>
                        </Text>
                      </StyledTd>
                      <StyledTd isEven={idx % 2 === 0} p="8px 14px">
                        <Text size="medium">{item.category}</Text>
                      </StyledTd>
                      <StyledTd isEven={idx % 2 === 0} p="8px 14px">
                        <Text size="medium">{arrayToString(item.tags)}</Text>
                      </StyledTd>
                      <StyledTd isEven={idx % 2 === 0} p="8px 14px">
                        <Text size="medium">
                          {item.isFeatured ? "true" : "false"}
                        </Text>
                      </StyledTd>
                      <StyledTd isEven={idx % 2 === 0} p="8px 14px">
                        <Text size="medium">{item.status}</Text>
                      </StyledTd>
                      <StyledTd isEven={idx % 2 === 0} p="8px 14px">
                        <Text size="medium">
                          {convertLocalDateTime(item.updatedTime)}
                        </Text>
                      </StyledTd>
                      <StyledTd
                        isEven={idx % 2 === 0}
                        className="leftAlign"
                        p="8px 14px"
                      >
                        <Flex
                          justifyContent="center"
                          cursor="pointer"
                          as="button"
                        >
                          {/* <Text>Action Here</Text> */}
                          <BlogActionMenu
                            item={item}
                            updateStatus={updateBlogStatus}
                          />
                        </Flex>
                      </StyledTd>
                    </StyledTr>
                  ))}
              </tbody>
            </StyledTable>
          ) : null}
        </>
      ) : null}
      <Flex justifyContent="center">
        <Pagination
          activePage={page}
          pageSize={PAGE_SIZE}
          totalItemCount={blogCount}
          pagesRange={5}
          onChange={setPage}
        />
      </Flex>
    </Box>
  );
}

export default BlogManagement;
